import { Head, router } from "@inertiajs/react"
import { CreditCard, Plus } from "lucide-react"
import { useState } from "react"

import {
  type IntentPayload,
  StripePaymentModal,
} from "@/components/billing/stripe-payment-modal"
import HeadingSmall from "@/components/heading-small"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsAccountPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Settings", href: settingsAccountPath() },
]

function csrfToken(): string {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") ?? ""
  )
}

async function sendJson<T>(
  url: string,
  method: string,
  body?: object,
): Promise<T> {
  const res = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = (await res.json().catch(() => ({}))) as { error?: string }
  if (!res.ok) throw new Error(data.error ?? "Something went wrong.")
  return data as T
}

type InvoiceStatus = "paid" | "pending" | "upcoming" | "failed"

interface PaymentMethod {
  brand: string | null
  last4: string | null
  exp_month: number | null
  exp_year: number | null
}

interface ActiveAgent {
  uid: string
  name: string
  started_label: string
  amount_cents: number
  icon: string | null
  outcome: string | null
  cancel_at: string | null
}

function formatShortDate(iso: string) {
  const [y, m, d] = iso.split("-").map(Number)
  return new Date(y, m - 1, d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

interface Invoice {
  uid: string
  date: string
  description: string
  amount_cents: number
  status: InvoiceStatus
}

interface NextPayment {
  due_date: string
  label: string
}

interface Props {
  active_agents: ActiveAgent[]
  monthly_total_cents: number
  next_payment: NextPayment | null
  invoices: Invoice[]
  payment_method: PaymentMethod | null
  stripe: { publishable_key: string; connected_account_id: string } | null
}

function fmt(cents: number) {
  return (
    "$" + (cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })
  )
}

function fmtExact(cents: number) {
  return (
    "$" +
    (cents / 100).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  )
}

const STATUS_BADGE: Record<
  InvoiceStatus,
  {
    label: string
    variant: "operational" | "draft" | "warning" | "destructive"
  }
> = {
  paid: { label: "Paid", variant: "operational" },
  pending: { label: "Pending", variant: "warning" },
  upcoming: { label: "Upcoming", variant: "draft" },
  failed: { label: "Failed", variant: "destructive" },
}

export default function BillingSettings({
  active_agents,
  monthly_total_cents,
  next_payment,
  invoices,
  payment_method,
  stripe,
}: Props) {
  const [cardModalOpen, setCardModalOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<ActiveAgent | null>(null)

  function fetchSetupIntent(): Promise<IntentPayload> {
    return sendJson<IntentPayload>("/settings/payment_method", "POST")
  }

  async function saveCard(setupIntentId: string) {
    await sendJson("/settings/payment_method", "PATCH", {
      setup_intent_id: setupIntentId,
    })
    router.reload({ only: ["payment_method"] })
  }

  // IMP-197: cancel/undo from the Active jobs list. Both redirect back to the
  // billing page, so Inertia reloads it with the updated state.
  function cancelAgent(uid: string) {
    router.post(`/agents/${uid}/cancel`)
  }

  function undoCancelAgent(uid: string) {
    router.post(`/agents/${uid}/undo_cancellation`)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Settings — Billing" />
      <SettingsLayout>
        <div className="space-y-10">
          {/* ── Current subscription ── */}
          <div className="space-y-4">
            <HeadingSmall
              title="Billing"
              description="Your subscription, payment method, and invoices."
            />

            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-muted-foreground text-sm">
                  Current subscription
                </p>
                <p className="mt-0.5 text-3xl font-medium">
                  {fmt(monthly_total_cents)}
                  <span className="text-muted-foreground text-base font-normal">
                    {" "}
                    / mo
                  </span>
                </p>
              </div>
              {next_payment && monthly_total_cents > 0 && (
                <div className="text-right">
                  <p className="text-muted-foreground text-sm">Next payment</p>
                  <p className="mt-0.5 text-sm font-medium">
                    {next_payment.label}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── Active jobs ── */}
          <div className="space-y-0">
            <h3 className="text-sm font-medium">Active jobs</h3>
            {active_agents.length === 0 ? (
              <div className="text-muted-foreground mt-4 rounded-lg border px-5 py-8 text-center text-sm">
                You don&rsquo;t have an active subscription yet.
              </div>
            ) : (
              <ul className="divide-y">
                {active_agents.map((agent) => (
                  <li
                    key={agent.uid}
                    className="flex items-center gap-4 py-4 last:pb-0"
                  >
                    <div className="flex-1">
                      <span className="text-sm font-medium">{agent.name}</span>
                      <p className="text-muted-foreground mt-0.5 text-sm">
                        Started {agent.started_label}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-medium tabular-nums">
                        {fmt(agent.amount_cents)}
                      </p>
                      <p className="text-muted-foreground text-sm">per month</p>
                    </div>
                    <div className="w-36 shrink-0 text-right">
                      {agent.cancel_at ? (
                        <>
                          <p className="text-xs whitespace-nowrap text-amber-700">
                            Cancels {formatShortDate(agent.cancel_at)}
                          </p>
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground text-xs underline"
                            onClick={() => undoCancelAgent(agent.uid)}
                          >
                            Undo
                          </button>
                        </>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setCancelTarget(agent)}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── Payment method ── */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Payment method</h3>
              {!payment_method && stripe && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCardModalOpen(true)}
                >
                  <Plus className="mr-1.5 size-3.5" />
                  Add card
                </Button>
              )}
            </div>

            {payment_method ? (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="bg-muted flex size-10 shrink-0 items-center justify-center rounded-md">
                    <CreditCard className="size-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium capitalize">
                      {payment_method.brand} •••• {payment_method.last4}
                    </p>
                    <p className="text-muted-foreground mt-0.5 text-sm">
                      Expires {payment_method.exp_month}/
                      {payment_method.exp_year}
                    </p>
                  </div>
                </div>
                {stripe && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setCardModalOpen(true)}
                  >
                    Update
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground rounded-lg border px-5 py-8 text-center text-sm">
                No card on file. Add one to keep your subscription active.
              </div>
            )}
          </div>

          {/* ── Payment history ── */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Payment history</h3>

            {invoices.length === 0 ? (
              <div className="text-muted-foreground rounded-lg border px-5 py-8 text-center text-sm">
                No payments yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm/6">
                  <thead>
                    <tr className="text-muted-foreground">
                      <th className="border-b pb-2 font-medium whitespace-nowrap">
                        Date
                      </th>
                      <th className="w-full border-b px-4 pb-2 font-medium">
                        Description
                      </th>
                      <th className="border-b px-4 pb-2 font-medium whitespace-nowrap">
                        Status
                      </th>
                      <th className="border-b px-4 pb-2 text-right font-medium whitespace-nowrap">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => {
                      const badge = STATUS_BADGE[invoice.status]
                      return (
                        <tr key={invoice.uid} className="hover:bg-muted/40">
                          <td className="text-muted-foreground border-b py-4 pr-4 whitespace-nowrap">
                            {invoice.date}
                          </td>
                          <td className="border-b px-4 py-4">
                            {invoice.description}
                          </td>
                          <td className="border-b px-4 py-4">
                            <Badge variant={badge.variant}>{badge.label}</Badge>
                          </td>
                          <td className="border-b px-4 py-4 text-right whitespace-nowrap tabular-nums">
                            {fmtExact(invoice.amount_cents)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </SettingsLayout>

      {stripe && (
        <StripePaymentModal
          open={cardModalOpen}
          onOpenChange={setCardModalOpen}
          title={
            payment_method ? "Update payment method" : "Add payment method"
          }
          description="Your card is stored securely with Stripe and used for monthly charges."
          mode="setup"
          submitLabel="Save card"
          fetchIntent={fetchSetupIntent}
          onConfirmed={saveCard}
        />
      )}

      <AlertDialog
        open={cancelTarget !== null}
        onOpenChange={(open) => {
          if (!open) setCancelTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel {cancelTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              It stays active
              {next_payment
                ? ` until ${next_payment.label}`
                : " until the end of the current billing period"}
              , then deactivates. You can undo anytime before then.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => {
                if (cancelTarget) cancelAgent(cancelTarget.uid)
                setCancelTarget(null)
              }}
            >
              Cancel subscription
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  )
}
